import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
import { productUnitSchema } from "../masterdata/products.js";
import { goodsReceiptStatusSchema, lotNoSchema, nonNegativeQuantitySchema, orderSuggestionReasonSchema, positiveQuantitySchema, purchaseOrderStatusSchema, purchaseUnitPriceSchema, receiptCostStatusSchema, } from "./enums.js";
/**
 * Market satın alma (Prompt 16 aşama B, ADR 0016 §9, §15): sipariş önerisi → tedarikçi siparişi (onay motoru
 * `MARKET_ORDER`) → mal kabul (irsaliye, barkodla okutma, parti + SKT). Mal kabul FİŞ ÜRETMEZ: alış fişini fatura
 * modülü keser (ADR 0016 §2); kabul miktarı ve GEÇİCİ maliyeti stok alt defterine işler.
 *
 * Birimler: miktar satış biriminde (3 ondalık), alış fiyatı KDV HARİÇ ₺ (4 ondalık), tutar kuruş dizgesi.
 */
// ── Sipariş önerisi ──────────────────────────────────────────────────────────
export const marketOrderSuggestionsQuerySchema = z.object({
    stationId: uuidSchema,
    supplierId: uuidSchema.optional().describe("Yalnızca bu tedarikçiden alınacak ürünler"),
    includeAll: z
        .enum(["true", "false"])
        .optional()
        .describe("true → önerisi olmayan (stoğu yeterli) ürünler de listelenir"),
});
export const marketOrderSuggestionItemSchema = z.object({
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    unit: productUnitSchema,
    categoryName: z.string().nullable(),
    supplierId: uuidSchema.nullable().describe("Tercihli (yoksa tek etkin) tedarikçi; bağ yoksa null"),
    supplierName: z.string().nullable(),
    onHand: quantitySchema.describe("Varsayılan depodaki miktar"),
    openOrderQuantity: quantitySchema.describe("Gönderilmiş ama henüz kabul edilmemiş sipariş miktarı (düşülür)"),
    pendingOrderQuantity: quantitySchema.describe("Taslak / onay bekleyen / onaylı ama gönderilmemiş siparişlerdeki miktar (bilgi; düşülmez)"),
    minStock: quantitySchema,
    maxStock: quantitySchema,
    dailySales: quantitySchema.describe("Günlük ortalama net satış (satış hızı penceresi)"),
    leadTimeDays: z.number().int(),
    packSize: quantitySchema.nullable(),
    minOrderQuantity: quantitySchema.nullable(),
    projectedAtArrival: quantitySchema.describe("Teslim anındaki tahmini stok"),
    daysOfCover: z.string().nullable().describe("Eldeki + açık sipariş kaç gün yeter (satış yoksa null)"),
    suggestedQuantity: quantitySchema,
    reason: orderSuggestionReasonSchema,
    unitPrice: unitPriceSchema.nullable().describe("Tedarikçi liste fiyatı (KDV hariç ₺)"),
    estimatedAmount: moneySchema.nullable().describe("Önerilen × liste fiyatı (kuruş)"),
});
export const marketOrderSuggestionSupplierSchema = z.object({
    supplierId: uuidSchema.nullable(),
    supplierName: z.string().nullable(),
    lineCount: z.number().int(),
    estimatedAmount: moneySchema,
});
export const marketOrderSuggestionListSchema = z.object({
    stationId: uuidSchema,
    warehouseId: uuidSchema.nullable().describe("Varsayılan depo (henüz açılmadıysa null)"),
    businessDate: localDateSchema,
    salesVelocityDays: z.number().int(),
    items: z.array(marketOrderSuggestionItemSchema),
    suppliers: z.array(marketOrderSuggestionSupplierSchema).describe("Tedarikçiye göre gruplanmış özet"),
});
// ── Tedarikçi siparişi ───────────────────────────────────────────────────────
export const purchaseOrderLineSchema = z.object({
    id: uuidSchema,
    lineNo: z.number().int(),
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    unit: productUnitSchema,
    quantity: quantitySchema,
    unitPrice: unitPriceSchema.describe("KDV hariç ₺"),
    vatRateCode: z.string(),
    lineAmount: moneySchema.describe("Miktar × birim fiyat (kuruş, KDV hariç)"),
    receivedQuantity: quantitySchema.describe("Kaydedilmiş mal kabullerden"),
    remainingQuantity: quantitySchema.describe("max(0, sipariş − kabul)"),
    suggestedQuantity: quantitySchema.nullable(),
});
export const purchaseOrderSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    warehouseId: uuidSchema,
    supplierId: uuidSchema,
    supplierName: z.string(),
    orderNo: z.string(),
    status: purchaseOrderStatusSchema,
    orderDate: localDateSchema,
    expectedDate: localDateSchema.nullable(),
    note: z.string().nullable(),
    netAmount: moneySchema.describe("KDV hariç tahmini tutar (kuruş)"),
    approvalRequestId: uuidSchema.nullable(),
    sentAt: timestampSchema.nullable(),
    closedReason: z.string().nullable().describe("Kapatma ya da iptal gerekçesi"),
    lineCount: z.number().int(),
    lines: z.array(purchaseOrderLineSchema).describe("Liste ucunda boş döner; ayrıntı ucunda dolu"),
    createdBy: uuidSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const purchaseOrderPageSchema = pageOf(purchaseOrderSchema);
export const listPurchaseOrdersQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    supplierId: uuidSchema.optional(),
    status: purchaseOrderStatusSchema.optional(),
});
const orderLinesInput = z
    .array(z.object({
    productId: uuidSchema,
    quantity: positiveQuantitySchema,
    unitPrice: purchaseUnitPriceSchema
        .nullable()
        .default(null)
        .describe("Boşsa tedarikçinin liste fiyatı (ürün – tedarikçi bağı)"),
}))
    .min(1)
    .max(999)
    .refine((lines) => new Set(lines.map((line) => line.productId)).size === lines.length, "Bir ürün siparişte yalnızca bir kez yer alabilir");
export const createPurchaseOrderSchema = z.object({
    stationId: uuidSchema,
    supplierId: uuidSchema.describe("Tedarikçi carisi (etkin; rolü tedarikçi ya da dağıtıcı)"),
    expectedDate: localDateSchema.nullable().default(null),
    note: z.string().trim().max(1000).nullable().default(null),
    lines: orderLinesInput,
});
/** TASLAK siparişi TAMAMEN değiştirir (satırlar dâhil). */
export const savePurchaseOrderSchema = z.object({
    expectedDate: localDateSchema.nullable().default(null),
    note: z.string().trim().max(1000).nullable().default(null),
    lines: orderLinesInput,
    version: z.number().int().positive(),
});
export const createOrdersFromSuggestionsSchema = z.object({
    stationId: uuidSchema,
    supplierId: uuidSchema.optional().describe("Yalnızca bu tedarikçinin siparişi"),
    productIds: z
        .array(uuidSchema)
        .max(999)
        .optional()
        .describe("Yalnızca bu ürünler (verilmezse önerisi olan tüm ürünler)"),
    items: z
        .array(z.object({ productId: uuidSchema, quantity: quantitySchema }))
        .min(1)
        .max(999)
        .optional()
        .describe("Kullanıcının düzenlediği miktarlar (verilirse `productIds` yerine geçer; öneri `suggestedQuantity` izi olarak kalır)"),
    expectedDate: localDateSchema.nullable().default(null),
    note: z.string().trim().max(1000).nullable().default(null),
});
export const ordersFromSuggestionsResultSchema = z.object({
    orders: z.array(purchaseOrderSchema).describe("Tedarikçi başına bir TASLAK sipariş"),
    skipped: z
        .array(z.object({ productId: uuidSchema, productName: z.string(), reason: z.string() }))
        .describe("Siparişe alınamayan öneriler (tedarikçi ya da liste fiyatı yok)"),
});
export const purchaseOrderActionSchema = z.object({ version: z.number().int().positive() });
export const purchaseOrderReasonActionSchema = z.object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(3, "Gerekçe yazın").max(500),
});
// ── Mal kabul ────────────────────────────────────────────────────────────────
export const goodsReceiptLineSchema = z.object({
    id: uuidSchema,
    lineNo: z.number().int(),
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    unit: productUnitSchema,
    trackExpiry: z.boolean(),
    orderLineId: uuidSchema.nullable(),
    expectedQuantity: quantitySchema.nullable().describe("Sipariş kalanı (siparişten açıldıysa)"),
    receivedQuantity: quantitySchema.describe("Sağlam, stoğa giren"),
    damagedQuantity: quantitySchema.describe("Hasarlı, stoğa GİRMEZ"),
    differenceQuantity: quantitySchema
        .nullable()
        .describe("Kabul − beklenen (negatif = eksik, pozitif = fazla); beklenen yoksa null"),
    scannedCount: z.number().int(),
    lotNo: z.string().nullable(),
    expiryDate: localDateSchema.nullable(),
    expiryWarning: z
        .enum(["REQUIRED", "PAST"])
        .nullable()
        .describe("SKT takipli üründe SKT eksik (REQUIRED) ya da geçmiş (PAST): kayıt 422 ile durur"),
    unitCost: unitPriceSchema.describe("GEÇİCİ birim maliyet, KDV hariç ₺"),
    finalUnitCost: z.string().nullable().describe("Faturayla eşleşince KESİN birim maliyet (₺, 6 ondalık)"),
    lineAmount: moneySchema.describe("Kabul × geçici maliyet (kuruş)"),
    note: z.string().nullable(),
});
export const goodsReceiptSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    warehouseId: uuidSchema,
    warehouseCode: z.string(),
    supplierId: uuidSchema,
    supplierName: z.string(),
    purchaseOrderId: uuidSchema.nullable(),
    purchaseOrderNo: z.string().nullable(),
    receiptNo: z.string(),
    despatchNo: z.string().nullable(),
    despatchDate: localDateSchema.nullable(),
    status: goodsReceiptStatusSchema,
    costStatus: receiptCostStatusSchema,
    receivedAt: timestampSchema,
    businessDate: localDateSchema,
    netAmount: moneySchema.describe("Kabul × geçici maliyet toplamı (kuruş); kayıtta hareket değerleri"),
    note: z.string().nullable(),
    postedAt: timestampSchema.nullable(),
    postedBy: uuidSchema.nullable(),
    cancelledReason: z.string().nullable(),
    matchId: uuidSchema.nullable().describe("Etkin fatura eşleşmesi"),
    totals: z.object({
        expectedQuantity: quantitySchema,
        receivedQuantity: quantitySchema,
        damagedQuantity: quantitySchema,
        shortQuantity: quantitySchema.describe("Σ max(0, beklenen − kabul)"),
        overQuantity: quantitySchema.describe("Σ max(0, kabul − beklenen); siparişli kabulde siparişte olmayan satır da fazladır, serbest kabulde 0"),
    }),
    lineCount: z.number().int(),
    lines: z.array(goodsReceiptLineSchema).describe("Liste ucunda boş döner; ayrıntı ucunda dolu"),
    createdBy: uuidSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const goodsReceiptPageSchema = pageOf(goodsReceiptSchema);
export const listGoodsReceiptsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    supplierId: uuidSchema.optional(),
    purchaseOrderId: uuidSchema.optional(),
    status: goodsReceiptStatusSchema.optional(),
    costStatus: receiptCostStatusSchema.optional(),
});
const despatchNoSchema = z.string().trim().min(1).max(40).describe("Tedarikçinin irsaliye numarası");
export const createGoodsReceiptSchema = z
    .object({
    stationId: uuidSchema.optional().describe("Serbest kabulde istasyon (varsayılan depoya girer)"),
    warehouseId: uuidSchema
        .optional()
        .describe("Serbest kabulde hedef depo (istasyonun etkin deposu; ör. yıkama kimyasal deposu). Verilmezse varsayılan depo"),
    purchaseOrderId: uuidSchema
        .optional()
        .describe("Siparişten kabul: satırlar sipariş KALANIYLA dolar (beklenen miktar)"),
    supplierId: uuidSchema.optional().describe("Serbest kabulde zorunlu; siparişte siparişin tedarikçisi"),
    despatchNo: despatchNoSchema.nullable().default(null),
    despatchDate: localDateSchema.nullable().default(null),
    note: z.string().trim().max(1000).nullable().default(null),
    fillReceived: z
        .boolean()
        .default(false)
        .describe("Siparişten açılırken kabul miktarını beklenenle doldur (okutmasız hızlı kabul)"),
})
    .refine((value) => value.purchaseOrderId !== undefined || (value.stationId && value.supplierId), "Sipariş seçin ya da istasyon ve tedarikçi girin");
export const updateGoodsReceiptSchema = patchOf(z.object({
    despatchNo: despatchNoSchema.nullable(),
    despatchDate: localDateSchema.nullable(),
    note: z.string().trim().max(1000).nullable(),
})).extend({ version: z.number().int().positive() });
const lineFields = {
    receivedQuantity: nonNegativeQuantitySchema,
    damagedQuantity: nonNegativeQuantitySchema.default("0"),
    lotNo: lotNoSchema.nullable().default(null),
    expiryDate: localDateSchema.nullable().default(null),
    unitCost: purchaseUnitPriceSchema
        .nullable()
        .default(null)
        .describe("Boşsa sipariş fiyatı, yoksa tedarikçi liste fiyatı; ikisi de yoksa zorunlu"),
    note: z.string().trim().max(300).nullable().default(null),
};
export const addGoodsReceiptLineSchema = z.object({
    productId: uuidSchema,
    ...lineFields,
    version: z.number().int().positive(),
});
/** Satır düzenleme: verilen alanlar değişir (kabul, hasarlı, parti, SKT, maliyet, not). */
export const updateGoodsReceiptLineSchema = patchOf(z.object(lineFields)).extend({
    version: z.number().int().positive().describe("Mal kabulün sürümü"),
});
export const goodsReceiptLineParamsSchema = z.object({ id: uuidSchema, lineId: uuidSchema });
export const createGoodsReceiptScanSchema = z.object({
    barcode: z.string().trim().min(1).max(48),
    quantity: positiveQuantitySchema.default("1").describe("Okutma başına miktar (varsayılan 1)"),
    damaged: z.boolean().default(false).describe("true → hasarlı miktara eklenir (stoğa girmez)"),
    lotNo: lotNoSchema.nullable().default(null).describe("Verilirse aynı partili satıra eklenir"),
    expiryDate: localDateSchema.nullable().default(null),
});
export const goodsReceiptScanResultSchema = z.object({
    created: z.boolean().describe("true → okutma yeni satır açtı; false → mevcut satırı artırdı"),
    line: goodsReceiptLineSchema,
    receiptVersion: z.number().int(),
});
export const goodsReceiptActionSchema = z.object({ version: z.number().int().positive() });
export const cancelGoodsReceiptSchema = z.object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(3, "İptal nedeni yazın").max(500),
});
//# sourceMappingURL=purchasing.js.map